import type { ReactNode } from 'react';
import { Button, Card, Drawer, Grid, Space, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './masterDetail.css';

/**
 * Master–detail layout for the list pages: the list stays on the left and the
 * selected row's full detail opens on the RIGHT of the same screen, so the
 * admin switches between people without leaving the list or losing its
 * filters. Selecting another row replaces the right pane.
 *
 * The caller owns the selection (it lives in the URL, see lib/urlState) and
 * passes the same `title` / `extra` / children it would have given a Drawer.
 *
 * Below the `lg` breakpoint there is no room for two panes, so the detail
 * falls back to the full-width Drawer the pages had before — same content,
 * same close behaviour.
 */
export function MasterDetail({
  open,
  onClose,
  title,
  extra,
  list,
  children,
  listWidth = '42%',
  drawerWidth = 820,
  closeLabel = 'Close details',
  chrome = true,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  extra?: ReactNode;
  /** The list (filters + table) — always rendered. */
  list: ReactNode;
  /** The detail of the selected row — rendered only while `open`. */
  children?: ReactNode;
  /** Width the list keeps while the detail is open (desktop). */
  listWidth?: string;
  /** Drawer width on small screens. */
  drawerWidth?: number;
  closeLabel?: string;
  /**
   * `false` when the detail renders its own header and close button (e.g. the
   * listener view): the pane is then a plain card and the small-screen drawer
   * has no title bar of its own.
   */
  chrome?: boolean;
}) {
  const screens = Grid.useBreakpoint();
  const { token } = theme.useToken();
  const inline = screens.lg === true;

  if (!inline) {
    return (
      <>
        {list}
        <Drawer
          width={drawerWidth}
          open={open}
          onClose={onClose}
          title={chrome ? title : undefined}
          extra={chrome ? extra : undefined}
          closeIcon={chrome ? undefined : false}
        >
          {children}
        </Drawer>
      </>
    );
  }

  return (
    <div className="master-detail" data-open={open ? 'true' : 'false'}>
      <div className="master-detail-list" style={{ flex: open ? `0 0 ${listWidth}` : '1 1 auto' }}>
        {list}
      </div>
      {open && (
        <aside className="master-detail-pane" aria-label="Details">
          {chrome ? (
          <Card
            title={title}
            extra={
              <Space wrap>
                {extra}
                <Button type="text" icon={<CloseOutlined />} aria-label={closeLabel} onClick={onClose} />
              </Space>
            }
            styles={{
              // The header stays put while the detail scrolls under it.
              header: { position: 'sticky', top: 0, zIndex: 2, background: token.colorBgContainer },
            }}
          >
            {children}
          </Card>
          ) : (
            <Card>{children}</Card>
          )}
        </aside>
      )}
    </div>
  );
}
